import type { TemplateResult } from '@umbraco-cms/backoffice/external/lit';
import type { UmbUfmRenderElement } from '../../../ufm/components/ufm-render/index.js';
import {
	css,
	customElement,
	html,
	ifDefined,
	keyed,
	nothing,
	property,
	ref,
	repeat,
	state,
	when,
} from '@umbraco-cms/backoffice/external/lit';
import { UmbLitElement } from '@umbraco-cms/backoffice/lit-element';
import { UmbSorterController } from '@umbraco-cms/backoffice/sorter';
import { UmbTextStyles } from '@umbraco-cms/backoffice/style';

export interface UmbTableItem {
	id: string;
	icon?: string | null;
	entityType?: string;
	data: Array<UmbTableItemData>;
	selectable?: boolean;
	active?: boolean;
	/**
	 * Overrides the table-wide select-only behaviour for this row.
	 * `true` always makes the row select-only, `false` always keeps its content interactive.
	 * When left `undefined`, the row follows the table: select-only while a selection is in progress, or when the table is configured as select-only.
	 */
	selectOnly?: boolean;
	/** When set, the row shows a children indicator. The nested options control what activating it does. */
	childrenIndicator?: {
		/** When set, the indicator becomes an anchor linking to this href. */
		href?: string;
		/** When set (and no `href` is provided), the indicator becomes a button invoking this callback. */
		onOpen?: () => void;
		/** Replaces the default expand symbol. The table still wraps it in the link or button the other options describe. */
		renderExpandSymbol?: () => TemplateResult;
	};
}

export interface UmbTableItemData {
	columnAlias: string;
	value: any;
}

export interface UmbTableColumn {
	name: string;
	/** Unique identifier for the column — must be unique within a given table instance, as it is used as the key for header and cell reconciliation. */
	alias: string;
	elementName?: string;
	width?: string;
	allowSorting?: boolean;
	align?: 'left' | 'center' | 'right';
	labelTemplate?: string;
	clipText?: boolean;
}

export interface UmbTableColumnLayoutElement extends HTMLElement {
	column: UmbTableColumn;
	item: UmbTableItem;
	value: any;
}

export interface UmbTableConfig {
	allowSelection: boolean;
	selectOnly?: boolean;
	allowSelectAll?: boolean;
	hideIcon?: boolean;
}

export class UmbTableSelectedEvent extends Event {
	#itemId: string | undefined;

	public constructor(args?: { itemId?: string }) {
		super('selected', { bubbles: true, composed: true });
		this.#itemId = args?.itemId;
	}

	public getItemId() {
		return this.#itemId;
	}
}

export class UmbTableDeselectedEvent extends Event {
	#itemId: string | undefined;

	public constructor(args?: { itemId: string }) {
		super('deselected', { bubbles: true, composed: true });
		this.#itemId = args?.itemId;
	}

	public getItemId() {
		return this.#itemId;
	}
}

export class UmbTableOrderedEvent extends Event {
	public constructor() {
		super('ordered', { bubbles: true, composed: true });
	}
}

export class UmbTableSortedEvent extends Event {
	#itemId: string;

	public constructor({ itemId }: { itemId: string }) {
		super('sorted', { bubbles: true, composed: true });
		this.#itemId = itemId;
	}

	public getItemId() {
		return this.#itemId;
	}
}

/**
 *  @element umb-table
 *  @description - Element for displaying a table
 *  @fires {UmbTableSelectedEvent} selected - fires when a row is selected
 *  @fires {UmbTableDeselectedEvent} deselected - fires when a row is deselected
 *  @fires {UmbTableOrderedEvent} sort - fires when a column order is changed
 *  @augments UmbLitElement
 */
@customElement('umb-table')
export class UmbTableElement extends UmbLitElement {
	/**
	 * Table Items
	 * @type {Array<UmbTableItem>}
	 * @memberof UmbTableElement
	 */
	@property({ type: Array, attribute: false })
	private _items: Array<UmbTableItem> = [];
	public get items(): Array<UmbTableItem> {
		return this._items;
	}
	public set items(value: Array<UmbTableItem>) {
		this._items = value;
		this.#sorter.setModel(value);
	}

	/**
	 * @description Table Columns
	 * @type {Array<UmbTableColumn>}
	 * @memberof UmbTableElement
	 */
	@property({ type: Array, attribute: false })
	public columns: Array<UmbTableColumn> = [];

	/**
	 * @description Table Config
	 * @type {UmbTableConfig}
	 * @memberof UmbTableElement
	 */
	@property({ type: Object, attribute: false })
	public config: UmbTableConfig = {
		allowSelection: false,
		selectOnly: false,
		hideIcon: false,
	};

	/**
	 * @description Table Selection
	 * @type {Array<string>}
	 * @memberof UmbTableElement
	 */
	@property({ type: Array, attribute: false })
	public selection: Array<string> = [];

	@property({ attribute: false })
	public onRowRendered?: (element: HTMLElement | undefined, item: UmbTableItem) => void;

	@property({ type: String, attribute: false })
	public orderingColumn = '';

	@property({ type: Boolean, attribute: false })
	public orderingDesc = false;

	private _sortable = false;
	@property({ type: Boolean, reflect: true })
	get sortable() {
		return this._sortable;
	}
	set sortable(newVal) {
		const oldVal = this._sortable;
		if (oldVal === newVal) return;
		this._sortable = newVal;

		if (this._sortable) {
			this.#sorter.enable();
		} else {
			this.#sorter.disable();
		}

		this.requestUpdate('sortable', oldVal);
	}

	@state()
	private _selectionMode = false;

	@state()
	private _columnConfigurationHash = '';

	@state()
	private _hasChildrenColumn = false;

	#cellElementCache = new WeakMap<UmbTableItem, Map<string, UmbTableColumnLayoutElement>>();

	#rowRenderedCallbacks = new Map<string, { fn: (el: Element | undefined) => void; current: UmbTableItem }>();

	#getRowRenderedCallback(item: UmbTableItem): (el: Element | undefined) => void {
		const existing = this.#rowRenderedCallbacks.get(item.id);
		if (existing) {
			existing.current = item;
			return existing.fn;
		}
		const entry = {
			current: item,
			fn: (el: Element | undefined) => this.onRowRendered?.(el as HTMLElement | undefined, entry.current),
		};
		this.#rowRenderedCallbacks.set(item.id, entry);
		return entry.fn;
	}

	override willUpdate(changedProperties: Map<string | number | symbol, unknown>) {
		super.willUpdate(changedProperties);
		if (changedProperties.has('selection')) {
			this._selectionMode = this.selection.length > 0;
		}
		if (changedProperties.has('_items')) {
			const currentIds = new Set(this._items.map((i) => i.id));
			for (const id of this.#rowRenderedCallbacks.keys()) {
				if (!currentIds.has(id)) this.#rowRenderedCallbacks.delete(id);
			}
			this._hasChildrenColumn = this._items.some((i) => i.childrenIndicator);
		}
		if (changedProperties.has('_items') || changedProperties.has('columns')) {
			this._columnConfigurationHash = JSON.stringify([
				this._hasChildrenColumn,
				...this.columns.map((column) => column.alias),
			]);
		}
	}

	override updated(changedProperties: Map<string | number | symbol, unknown>) {
		super.updated(changedProperties);

		// The `keyed` directive in `render()` rebuilds the `<uui-table>` element when the column
		// configuration changes. The sorter caches its container element on first initialization, so
		// when the table is replaced we need to reattach it to the fresh node.
		if (this._sortable && changedProperties.has('_columnConfigurationHash')) {
			this.#sorter.disable();
			this.#sorter.enable();
		}
	}

	#sorter = new UmbSorterController<UmbTableItem>(this, {
		getUniqueOfElement: (element) => {
			return element.dataset.sortableId;
		},
		getUniqueOfModel: (item) => {
			return item.id;
		},
		identifier: 'Umb.SorterIdentifier.UmbTable',
		itemSelector: 'uui-table-row',
		containerSelector: 'uui-table',
		onChange: ({ model }) => {
			const oldValue = this.items;
			this.items = model;
			this.requestUpdate('items', oldValue);
		},
		onEnd: ({ item }) => {
			this.dispatchEvent(new UmbTableSortedEvent({ itemId: item.id }));
		},
	});

	constructor() {
		super();
		this.#sorter.disable();
	}

	private _isSelected(key: string) {
		return this.selection.includes(key);
	}

	#isSelectableItem(item: UmbTableItem) {
		return item.selectable !== false;
	}

	private _handleRowCheckboxChange(event: Event, item: UmbTableItem) {
		const checkboxElement = event.target as HTMLInputElement;
		if (checkboxElement.checked) {
			this._selectRow(item);
		} else {
			this._deselectRow(item);
		}
	}

	private _handleAllRowsCheckboxChange(event: Event) {
		const checkboxElement = event.target as HTMLInputElement;
		if (checkboxElement.checked) {
			this._selectAllRows();
		} else {
			this._deselectAllRows();
		}
	}

	private _handleOrderingChange(column: UmbTableColumn) {
		this.orderingDesc = this.orderingColumn === column.alias ? !this.orderingDesc : false;
		this.orderingColumn = column.alias;
		this.dispatchEvent(new UmbTableOrderedEvent());
	}

	private _selectAllRows() {
		if (this.config.allowSelectAll === false) {
			throw new Error('Select all is not allowed in the current table configuration.');
		}

		// Merge the current page's selectable rows into the existing selection so selections
		// accumulate across pages rather than replacing the previous page's selection.
		const currentPageIds = this.items.filter((item) => this.#isSelectableItem(item)).map((item) => item.id);
		this.selection = [...new Set([...this.selection, ...currentPageIds])];
		this._selectionMode = true;
		this.dispatchEvent(new UmbTableSelectedEvent());
	}

	private _deselectAllRows() {
		if (this.config.allowSelectAll === false) {
			throw new Error('Select all is not allowed in the current table configuration.');
		}

		// Only remove the current page's rows, leaving any selection from other pages intact.
		const currentPageIds = new Set(this.items.map((item) => item.id));
		this.selection = this.selection.filter((id) => !currentPageIds.has(id));
		this._selectionMode = this.selection.length > 0;
		this.dispatchEvent(new UmbTableDeselectedEvent());
	}

	private _selectRow(item: UmbTableItem) {
		const isSelectble = this.#isSelectableItem(item);
		if (!isSelectble) {
			throw new Error(`Item with id ${item.id} is not selectable.`);
		}

		this.selection = [...this.selection, item.id];
		this._selectionMode = this.selection.length > 0;
		this.dispatchEvent(new UmbTableSelectedEvent({ itemId: item.id }));
	}

	private _deselectRow(item: UmbTableItem) {
		this.selection = this.selection.filter((selectionKey) => selectionKey !== item.id);
		this._selectionMode = this.selection.length > 0;
		this.dispatchEvent(new UmbTableDeselectedEvent({ itemId: item.id }));
	}

	override render() {
		const iconColumnWidth = this._hasChildrenColumn ? '45px' : '60px';
		const style = !(this.config.allowSelection === false && this.config.hideIcon === true)
			? `width: ${iconColumnWidth}`
			: undefined;
		// Firefox's `display: table-*` engine does not reliably relayout when cells are
		// inserted or removed from existing rows. Key the whole table on the column
		// configuration so the table is rebuilt whenever the column set changes.
		return keyed(
			this._columnConfigurationHash,
			html`
				<uui-table class="uui-text">
					${this._hasChildrenColumn ? html`<uui-table-column style="width: 24px;"></uui-table-column>` : nothing}
					<uui-table-column style=${ifDefined(style)}></uui-table-column>
					<uui-table-head>
						${this._hasChildrenColumn
							? html`<uui-table-head-cell class="children-indicator-cell"></uui-table-head-cell>`
							: nothing}
						${this._renderHeaderCheckboxCell()}
						${repeat(
							this.columns,
							(column) => column.alias,
							(column) => this._renderHeaderCell(column),
						)}
					</uui-table-head>
					${repeat(this.items, (item) => item.id, this._renderRow)}
				</uui-table>
			`,
		);
	}

	private _renderHeaderCell(column: UmbTableColumn) {
		return html`
			<uui-table-head-cell style="--uui-table-cell-padding: 0 var(--uui-size-5); text-align:${column.align ?? 'left'};">
				${column.allowSorting ? html`${this._renderSortingUI(column)}` : html`<span>${column.name}</span>`}
			</uui-table-head-cell>
		`;
	}

	private _renderSortingUI(column: UmbTableColumn) {
		return html`
			<button
				style="padding: var(--uui-size-5) var(--uui-size-1);"
				@click="${() => this._handleOrderingChange(column)}">
				<span>${column.name}</span>
				<uui-symbol-sort ?active=${this.orderingColumn === column.alias} ?descending=${this.orderingDesc}>
				</uui-symbol-sort>
			</button>
		`;
	}

	private _renderHeaderCheckboxCell() {
		if (this.config.hideIcon && !this.config.allowSelection) return;
		// Compute the header state against the current page only — the selection can span multiple
		// pages, so comparing its total length against the page size gives the wrong state.
		const selectableIds = this.items.filter((item) => this.#isSelectableItem(item)).map((item) => item.id);
		const selectionSet = new Set(this.selection);
		const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectionSet.has(id));
		const indeterminate = !allSelected && selectableIds.some((id) => selectionSet.has(id));
		return html`
			<uui-table-head-cell style="--uui-table-cell-padding: 0; text-align: center;">
				${when(
					this.config.allowSelection && this.config.allowSelectAll !== false,
					() => html`
						<uui-checkbox
							aria-label=${this.localize.term('general_selectAll')}
							style="padding: var(--uui-size-4) var(--uui-size-5);"
							@change="${this._handleAllRowsCheckboxChange}"
							?checked=${allSelected}
							?indeterminate=${indeterminate}></uui-checkbox>
					`,
				)}
			</uui-table-head-cell>
		`;
	}

	private _renderRow = (item: UmbTableItem) => {
		const isItemSelectable = this.#isSelectableItem(item);
		const selectionMode = this._selectionMode || this.config.selectOnly === true;
		return html`
			<uui-table-row
				${ref(this.#getRowRenderedCallback(item))}
				data-sortable-id=${item.id}
				?selectable=${this.config.allowSelection && !this._sortable && isItemSelectable}
				?data-selection-mode=${selectionMode}
				?select-only=${item.selectOnly ?? selectionMode}
				?selected=${this._isSelected(item.id)}
				?active=${item.active ?? false}
				@selected=${() => this._selectRow(item)}
				@deselected=${() => this._deselectRow(item)}>
				${this._hasChildrenColumn
					? html`<uui-table-cell class="children-indicator-cell">
							${this.#renderChildrenIndicator(item)}
						</uui-table-cell>`
					: nothing}
				${this._renderRowCheckboxCell(item)}
				${repeat(
					this.columns,
					(column) => column.alias,
					(column) => this._renderRowCell(column, item),
				)}
			</uui-table-row>
		`;
	};

	#renderChildrenIndicator(item: UmbTableItem) {
		const indicator = item.childrenIndicator;
		if (!indicator) return nothing;

		const symbol = indicator.renderExpandSymbol?.() ?? html`<uui-symbol-expand></uui-symbol-expand>`;

		if (indicator.href) {
			return html`
				<uui-button
					compact
					label=${this.localize.term('general_open')}
					href=${indicator.href}
					data-mark="table-row:open">
					${symbol}
				</uui-button>
			`;
		}

		if (indicator.onOpen) {
			return html`
				<uui-button
					compact
					label=${this.localize.term('general_open')}
					data-mark="table-row:open"
					@click=${(e: Event) => {
						e.stopPropagation();
						indicator.onOpen?.();
					}}>
					${symbol}
				</uui-button>
			`;
		}

		return symbol;
	}

	private _renderRowCheckboxCell(item: UmbTableItem) {
		if (this.sortable === true) {
			return html`
				<uui-table-cell style="text-align: center;">
					<uui-icon name="icon-grip"></uui-icon>
				</uui-table-cell>
			`;
		}

		if (this.config.hideIcon && !this.config.allowSelection) return;

		const isItemSelectable = this.#isSelectableItem(item);

		return html`
			<uui-table-cell style="text-align: center;">
				${when(!this.config.hideIcon, () => html`<umb-icon name="${ifDefined(item.icon ?? undefined)}"></umb-icon>`)}
				${when(
					this.config.allowSelection && isItemSelectable,
					() => html`
						<uui-checkbox
							aria-label=${this.localize.term('buttons_select')}
							@click=${(e: PointerEvent) => e.stopPropagation()}
							@change=${(event: Event) => this._handleRowCheckboxChange(event, item)}
							?checked=${this._isSelected(item.id)}></uui-checkbox>
					`,
				)}
			</uui-table-cell>
		`;
	}

	private _renderRowCell(column: UmbTableColumn, item: UmbTableItem) {
		return html`
			<uui-table-cell
				style="--uui-table-cell-padding: 0 var(--uui-size-5); text-align:${column.align ??
				'left'}; width: ${column.width || 'auto'};"
				?clip-text=${column.clipText}>
				${this._renderCellContent(column, item)}
			</uui-table-cell>
		`;
	}

	private _renderCellContent(column: UmbTableColumn, item: UmbTableItem) {
		const value = item.data.find((data) => data.columnAlias === column.alias)?.value;

		if (column.elementName) {
			let itemCache = this.#cellElementCache.get(item);
			if (!itemCache) {
				itemCache = new Map();
				this.#cellElementCache.set(item, itemCache);
			}

			let element = itemCache.get(column.alias);
			if (!element || element.tagName.toLowerCase() !== column.elementName.toLowerCase()) {
				element = document.createElement(column.elementName) as UmbTableColumnLayoutElement;
				itemCache.set(column.alias, element);
			}

			element.column = column;
			element.item = item;
			element.value = value;
			return element;
		}

		if (column.labelTemplate) {
			import('@umbraco-cms/backoffice/ufm');
			const element = document.createElement('umb-ufm-render') as UmbUfmRenderElement;
			element.inline = true;
			element.markdown = column.labelTemplate;
			element.value = { value };
			return element;
		}

		return value;
	}

	static override styles = [
		UmbTextStyles,
		css`
			:host {
				height: fit-content;
			}

			:host([sortable]) {
				uui-table-row:hover {
					cursor: grab;
				}

				uui-table-row:active {
					cursor: grabbing;
				}
			}

			uui-table-row uui-checkbox {
				display: none;
			}

			uui-table-row[selectable]:focus uui-table-cell:not(.children-indicator-cell) umb-icon,
			uui-table-row[selectable]:focus-within uui-table-cell:not(.children-indicator-cell) umb-icon,
			uui-table-row[selectable]:hover uui-table-cell:not(.children-indicator-cell) umb-icon,
			uui-table-row[data-selection-mode] uui-table-cell:not(.children-indicator-cell) umb-icon {
				display: none;
			}

			uui-table-row[selectable]:focus uui-checkbox,
			uui-table-row[selectable]:focus-within uui-checkbox,
			uui-table-row[selectable]:hover uui-checkbox,
			uui-table-row[data-selection-mode] uui-checkbox {
				display: inline-block;
			}

			.children-indicator-cell {
				padding-right: 0;
				display: flex;
				align-items: center;
			}

			.children-indicator-cell uui-button {
				--uui-button-padding-left-factor: 0;
				--uui-button-padding-right-factor: 0;
			}

			uui-table-head-cell:focus,
			uui-table-head-cell:focus-within,
			uui-table-head-cell:hover {
				--uui-symbol-sort-hover: 1;
			}

			uui-table-head-cell button {
				padding: 0;
				background-color: transparent;
				color: inherit;
				border: none;
				cursor: pointer;
				font-weight: inherit;
				font-size: inherit;
				display: inline-flex;
				align-items: center;
				justify-content: space-between;
				width: 100%;
				text-align: inherit;
			}

			uui-table-head-cell button > span {
				flex: 1 0 auto;
			}

			uui-table-cell umb-icon {
				vertical-align: top;
			}
		`,
	];
}

export default UmbTableElement;

declare global {
	interface HTMLElementTagNameMap {
		'umb-table': UmbTableElement;
	}
}
